import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { AlertCircle, CheckCircle, Loader2, Key } from 'lucide-react';
import { API_BASE_URL } from '../config';

const API = API_BASE_URL + '/api';

export function SetPasswordPage() {
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenData, setTokenData] = useState(null);

  useEffect(() => {
    // Get token from URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    
    if (tokenParam) {
      setToken(tokenParam);
      verifyToken(tokenParam);
    } else {
      setError('Enginn hlekkur fannst. Vinsamlegast athugaðu að þú hafir notað réttan hlekk.');
      setVerifying(false);
    }
  }, []);

  const verifyToken = async (tokenValue) => {
    try {
      const response = await axios.get(`${API}/auth/verify-token/${tokenValue}`);
      
      if (response.data.valid) {
        setTokenData(response.data);
      } else {
        setError(response.data.error || 'Ógildur hlekkur');
      }
    } catch (err) {
      setError('Villa við að sannreyna hlekk. Vinsamlegast reyndu aftur.');
    } finally {
      setVerifying(false);
    }
  };

  const validatePassword = () => {
    if (password.length < 8) {
      setError('Lykilorð verður að vera að minnsta kosti 8 stafir');
      return false;
    }
    
    if (password !== confirmPassword) {
      setError('Lykilorð passa ekki saman');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validatePassword()) {
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/set-password`, {
        token: token,
        password: password
      });

      if (response.data.status === 'success') {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Villa kom upp. Vinsamlegast reyndu aftur.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <p className="text-slate-600">Staðfesti hlekk...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !tokenData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-red-600">Villa</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
            <Button 
              onClick={() => window.location.href = '/'}
              className="w-full"
            >
              Fara á innskráningarsíðu
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-green-600 flex items-center justify-center gap-2">
              <CheckCircle className="h-6 w-6" />
              Tókst!
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-green-800 font-medium">
                  Lykilorð hefur verið sett
                </p>
                <p className="text-sm text-green-700 mt-1">
                  Þú getur nú skráð þig inn með nýja lykilorðinu þínu.
                </p>
                <p className="text-xs text-green-600 mt-2">
                  Færir þig sjálfkrafa á innskráningarsíðu...
                </p>
              </div>
            </div>
            <Button 
              onClick={() => window.location.href = '/'}
              className="w-full"
            >
              Fara á innskráningarsíðu
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-slate-800 flex items-center justify-center gap-2">
            <Key className="h-6 w-6 text-blue-600" />
            {tokenData?.type === 'invitation' ? 'Velkomin/n!' : 'Endurstilla lykilorð'}
          </CardTitle>
          <p className="text-slate-600 mt-2">
            {tokenData?.type === 'invitation' 
              ? `Halló ${tokenData?.user_name}, búðu til lykilorð fyrir aðganginn þinn.`
              : `Halló ${tokenData?.user_name}, búðu til nýtt lykilorð.`
            }
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Nýtt lykilorð</Label>
              <Input
                id="password"
                type="password"
                placeholder="Að minnsta kosti 8 stafir"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />
              <p className="text-xs text-slate-500">
                Lykilorð verður að vera að minnsta kosti 8 stafir
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Staðfesta lykilorð</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="Sláðu inn lykilorð aftur"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-md">
                <AlertCircle className="h-4 w-4 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full h-12"
              disabled={loading || !password || !confirmPassword}
            >
              {loading ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                tokenData?.type === 'invitation' ? 'Ljúka skráningu' : 'Endurstilla lykilorð'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
